import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Plus } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, FormDialog, KanbanBoard, PageHeader,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { useEmployeeOptions } from '@/features/employee'
import { toApiProblem } from '@/lib/api/client'
import { useProCaseMutations, useProCases, useProTemplates } from '../api'
import { PRO_BOARD_COLUMNS, PRO_SOURCE_LABEL, PRO_STATUS_LABEL, type ProCaseStatus } from '../types'
import { Select } from '@/components/ui/select'

/**
 * The case board.
 *
 * <p>Columns, not drag-and-drop. Moving a case is a decision somebody made — a select on the
 * card records it as one, works with a keyboard, and cannot be done by accident with a
 * stray gesture. The board is a view of `status`; what changes it is an explicit call.
 */
export function ProBoardPage() {
  useBreadcrumb()
  const templates = useProTemplates()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const employees = useEmployeeOptions()
  const [templateId, setTemplateId] = useState('')
  const [entityId, setEntityId] = useState('')

  const cases = useProCases(undefined, undefined, templateId || undefined)
  const { open, move } = useProCaseMutations()

  const [opening, setOpening] = useState(false)
  const [draft, setDraft] = useState({ templateId: '', employeeId: '' })
  const [error, setError] = useState<string | null>(null)

  const visible = useMemo(
    () => (cases.data ?? []).filter((row) => entityId === '' || row.legalEntityId === entityId),
    [cases.data, entityId],
  )

  const byColumn = useMemo(() => {
    const grouped = new Map<ProCaseStatus, typeof visible>()
    PRO_BOARD_COLUMNS.forEach((status) => grouped.set(status, []))
    visible.forEach((row) => grouped.get(row.status)?.push(row))
    return grouped
  }, [visible])

  async function openCase() {
    setError(null)
    try {
      await open.mutateAsync({
        templateId: draft.templateId,
        employeeId: draft.employeeId || null,
      })
      setOpening(false)
      setDraft({ templateId: '', employeeId: '' })
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That case could not be opened')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="PRO cases"
        subtitle="Government-facing work: what is open, who has it, and what is overdue."
        actions={
          <Button type="button" onClick={() => { setError(null); setOpening(true) }}>
            <Plus className="size-4" aria-hidden="true" />
            Open a case
          </Button>
        }
      />

      <FilterBar>
        <Select
          aria-label="Process"
          id="board-template" value={templateId}
          onChange={(event) => setTemplateId(event.target.value)}
          className="min-w-52"
        >
          <option value="">Every process</option>
          {(templates.data ?? []).map((template) => (
            <option key={template.id} value={template.id}>{template.name}</option>
          ))}
        </Select>
        <Select
          aria-label="Legal entity"
          id="board-entity" value={entityId}
          onChange={(event) => setEntityId(event.target.value)}
          className="min-w-48"
        >
          <option value="">Every entity</option>
          {(entities.data ?? []).map((entity) => (
            <option key={entity.id} value={entity.id}>{entity.name}</option>
          ))}
        </Select>
      </FilterBar>

      {cases.isPending && <Skeleton className="h-64 w-full rounded-[var(--radius-card)]" />}
      {cases.isError && <ErrorState error={cases.error} onRetry={() => void cases.refetch()} />}
      {cases.isSuccess && visible.length === 0 && (
        <Card className="p-6">
          <EmptyState
            title="No cases on the board"
            description="Open one, or let a process with a renewal lead time open its own before a document expires."
          />
        </Card>
      )}

      {visible.length > 0 && (
        <KanbanBoard
          aria-label="PRO cases by status"
          hideEmptyColumns
          columns={PRO_BOARD_COLUMNS.map((status) => ({
            id: status,
            label: PRO_STATUS_LABEL[status],
            items: byColumn.get(status) ?? [],
          }))}
          itemKey={(row) => row.id}
          renderCard={(row) => (
            <>
              <Link
                to={`/pro/cases/${row.id}`}
                className="block text-sm font-medium text-[var(--color-heading)] hover:underline"
              >
                {row.caseNo} · {row.templateName}
              </Link>
              <p className="text-xs text-[var(--color-muted)]">
                {row.employeeName ?? row.candidateRef ?? 'The company'}
                {row.targetDate && (
                  <>
                    {' '}· due <DateValue value={row.targetDate} />
                  </>
                )}
              </p>
              {row.overdue && (
                <p className="text-xs font-medium text-[var(--color-danger)]">Overdue</p>
              )}
              {row.source !== 'MANUAL' && (
                <p className="text-xs text-[var(--color-muted)]">{PRO_SOURCE_LABEL[row.source]}</p>
              )}
            </>
          )}
          renderMove={(row) => (
            <>
              <label
                htmlFor={`move-${row.id}`}
                className="block text-xs text-[var(--color-muted)]"
              >
                Move {row.caseNo} to
              </label>
              <Select
                id={`move-${row.id}`}
                value={row.status}
                onChange={(event) =>
                  move.mutate({ id: row.id, status: event.target.value as ProCaseStatus })
                }
                className="w-full"
              >
                {PRO_BOARD_COLUMNS.map((option) => (
                  <option key={option} value={option}>{PRO_STATUS_LABEL[option]}</option>
                ))}
              </Select>
            </>
          )}
        />
      )}

      <FormDialog
        open={opening}
        onOpenChange={setOpening}
        title="Open a PRO case"
        description="Its steps and checklist are copied from the process now, and kept — editing the process later will not rewrite this case."
        submitLabel="Open the case"
        loading={open.isPending}
        error={error}
        onSubmit={() => void openCase()}
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="open-template">Process</Label>
            <Select
              id="open-template" required value={draft.templateId}
              onChange={(event) => setDraft({ ...draft, templateId: event.target.value })}
              className="w-full"
            >
              <option value="">Choose a process…</option>
              {(templates.data ?? []).filter((t) => t.active).map((template) => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="open-employee">For whom</Label>
            <Select
              id="open-employee" value={draft.employeeId}
              onChange={(event) => setDraft({ ...draft, employeeId: event.target.value })}
              className="w-full"
            >
              <option value="">The company itself</option>
              {(employees.data ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>{employee.displayName}</option>
              ))}
            </Select>
          </div>
        </div>
      </FormDialog>
    </div>
  )
}
